import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { referralService } from '../../../services/referralService';

const ReferralDetailModal = ({ referral, onClose, onStatusUpdate, onNotesUpdate, onRefresh }) => {
  const [notesDraft, setNotesDraft] = useState(referral?.internalNotes ?? '');
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(referral?.internalNotesUpdatedAt ?? null);
  const [selectedStatus, setSelectedStatus] = useState(referral?.status);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showRevisionHistory, setShowRevisionHistory] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [loadingRevisions, setLoadingRevisions] = useState(false);
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [showUrgencyModal, setShowUrgencyModal] = useState(false);
  const [selectedUrgency, setSelectedUrgency] = useState(referral?.urgency || 'routine');
  const [urgencySummary, setUrgencySummary] = useState(referral?.urgency_summary || '');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  const canMarkUrgent = true; // Permission check - can be enhanced with user role checks

  // Sync draft state when modal opens or referral changes
  useEffect(() => {
    setNotesDraft(referral?.internalNotes ?? '');
    setLastSavedAt(referral?.internalNotesUpdatedAt ?? null);
    setSelectedStatus(referral?.status);
  }, [referral?.id, referral?.status]);

  const statusOptions = [
    { value: 'new', label: 'Nouveau' },
    { value: 'reviewed', label: 'En revue' },
    { value: 'scheduled', label: 'Programmé' },
    { value: 'completed', label: 'Terminé' },
    { value: 'cancelled', label: 'Annulé' }
  ];

  const handleSaveNotes = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);
      
      // Call service to update notes in database
      const updatedReferral = await referralService?.updateNotes(referral?.id, notesDraft);
      
      if (!updatedReferral) {
        throw new Error('La mise à jour n\'a pas retourné de données');
      }
      
      // Update local state with returned data
      const timestamp = updatedReferral?.internalNotesUpdatedAt ?? new Date()?.toISOString();
      setLastSavedAt(timestamp);
      
      // Show success feedback
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
      // Notify parent component to update its state
      if (onNotesUpdate) {
        onNotesUpdate(referral?.id, notesDraft, timestamp);
      }
    } catch (error) {
      console.error('Error saving notes:', error);
      const errorMessage = error?.message || 'Erreur inconnue';
      alert(`Erreur lors de la sauvegarde des notes: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleViewRevisionHistory = async () => {
    setShowRevisionHistory(true);
    setLoadingRevisions(true);
    try {
      const revisionData = await referralService?.getNotesRevisions(referral?.id);
      setRevisions(revisionData || []);
    } catch (error) {
      console.error('Error loading revision history:', error);
      alert('Erreur lors du chargement de l\'historique');
    } finally {
      setLoadingRevisions(false);
    }
  };

  const handleCloseRevisionHistory = () => {
    setShowRevisionHistory(false);
    setSelectedRevision(null);
  };

  const handleViewRevision = (revision) => {
    setSelectedRevision(revision);
  };

  const handleRestoreRevision = (revision) => {
    setNotesDraft(revision?.content || '');
    setShowRevisionHistory(false);
    setSelectedRevision(null);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      setSaving(true);
      await onStatusUpdate(referral?.id, newStatus);
      setSelectedStatus(newStatus);
      await onRefresh();
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUrgency = async () => {
    try {
      setSaving(true);
      const updatedReferral = await referralService?.updateUrgency(referral?.id, selectedUrgency, urgencySummary);
      if (!updatedReferral) {
        throw new Error('La mise à jour n\'a pas retourné de données');
      }
      await onRefresh();
      setShowUrgencyModal(false);
    } catch (error) {
      console.error('Error updating urgency:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);

      // Validate PDF path exists
      if (!referral?.pdfPath) {
        alert('Le chemin du PDF est manquant. Veuillez contacter le support.');
        return;
      }

      console.log('📥 Downloading PDF from path:', referral?.pdfPath);

      // Get signed URL for PDF
      const signedUrl = await referralService?.getPdfSignedUrl(referral?.pdfPath);

      if (!signedUrl) {
        throw new Error('URL de téléchargement non générée');
      }

      console.log('✅ Opening PDF in new tab');

      // Open PDF in new tab
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('❌ Error downloading PDF:', error);
      const errorMessage = error?.message || 'Erreur inconnue lors du téléchargement';
      
      // Provide user-friendly error messages
      if (errorMessage?.includes('existe pas dans le stockage') || errorMessage?.includes('expiré')) {
        alert(
          'Le document PDF n\'est plus disponible.\n\n' +
          'Raisons possibles:\n'+ '• Le PDF a expiré (conservation de 90 jours)\n'+ '• Le PDF n\'a jamais été créé lors de la soumission\n' + '• Le fichier a été supprimé\n\n'+ 'Veuillez contacter le dentiste référent pour obtenir une nouvelle copie du dossier.'
        );
      } else {
        alert(`Erreur lors du téléchargement du PDF: ${errorMessage}`);
      }
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Check if PDF is expired (after 90 days)
  const isPdfExpired = () => {
    if (!referral?.pdfExpiresAt) return false;
    return new Date(referral.pdfExpiresAt) < new Date();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMMM yyyy à HH:mm', { locale: fr });
    } catch {
      return '-';
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return null;
    try {
      return format(new Date(dateString), 'HH:mm', { locale: fr });
    } catch {
      return null;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div
          className="bg-card rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-white/8"
          onClick={(e) => e?.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-card/95 backdrop-blur-xl border-b border-white/8 p-6 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-primary">
                Détails de l'adressage
              </h2>
              <p className="text-sm text-muted mt-1">
                Soumis le {formatDate(referral?.submissionDate)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-xl bg-card-elevated hover:bg-card-hover flex items-center justify-center transition-colors border border-white/8"
            >
              <Icon name="X" size={20} className="text-secondary" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Status Update */}
            <div className="bg-card-elevated border border-white/8 p-4 rounded-xl">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Select
                    label="Statut"
                    options={statusOptions}
                    value={selectedStatus}
                    onChange={handleStatusChange}
                  />
                </div>
                {referral?.urgency && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-muted">Niveau d'urgence</p>
                      {canMarkUrgent && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowUrgencyModal(true)}
                          className="text-xs h-7 px-2"
                        >
                          <Icon name="Edit2" size={12} className="mr-1" />
                          Modifier
                        </Button>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        {referral?.urgency === 'emergency' && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-error/10 border border-error/30 shadow-sm">
                            <Icon name="AlertTriangle" size={16} className="text-error" />
                            <span className="text-sm font-semibold text-error">Urgence</span>
                          </div>
                        )}
                        {referral?.urgency === 'urgent' && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-warning/10 border border-warning/30 shadow-sm">
                            <Icon name="AlertCircle" size={16} className="text-warning" />
                            <span className="text-sm font-semibold text-warning">Urgent</span>
                          </div>
                        )}
                        {referral?.urgency === 'routine' && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted/10 border border-muted/30">
                            <Icon name="Circle" size={16} className="text-muted-foreground" />
                            <span className="text-sm font-semibold text-muted-foreground">Routine</span>
                          </div>
                        )}
                        <div className="group relative">
                          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/30">
                            <Icon name="Sparkles" size={12} className="text-purple-400" />
                            <span className="text-[10px] font-semibold text-purple-400">IA</span>
                          </div>
                          <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-card-elevated border border-white/10 rounded text-xs text-secondary whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            Classifié par ChatGPT
                          </div>
                        </div>
                      </div>
                      {referral?.urgency_summary && (
                        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-purple-500/5 border border-purple-500/20">
                          <Icon name="Info" size={14} className="text-purple-400 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-secondary italic leading-relaxed">
                            {referral?.urgency_summary}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Patient Information */}
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                <Icon name="User" size={20} className="text-accent" />
                Informations patient
              </h3>
              <div className="bg-card-elevated border border-white/8 p-4 rounded-xl space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted mb-1">Nom complet</p>
                    <p className="text-sm font-medium text-secondary">
                      {referral?.patientFirstName} {referral?.patientLastName}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted mb-1">Année de naissance</p>
                    <p className="text-sm font-medium text-secondary">{referral?.patientYearOfBirth || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted mb-1">Sexe</p>
                    <p className="text-sm font-medium text-secondary">{referral?.patientSex || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted mb-1">Téléphone</p>
                    <p className="text-sm font-medium text-secondary">{referral?.patientPhone || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted mb-1">Email</p>
                    <p className="text-sm font-medium text-secondary">{referral?.patientEmail || '-'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Referring Dentist */}
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                <Icon name="Stethoscope" size={20} className="text-accent" />
                Dentiste adresseur
              </h3>
              <div className="bg-card-elevated border border-white/8 p-4 rounded-xl space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted mb-1">Nom</p>
                    <p className="text-sm font-medium text-secondary">{referral?.referringDentistName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted mb-1">Cabinet</p>
                    <p className="text-sm font-medium text-secondary">{referral?.referringPracticeName || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted mb-1">Email</p>
                    <p className="text-sm font-medium text-secondary">{referral?.referringDentistEmail || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted mb-1">Téléphone</p>
                    <p className="text-sm font-medium text-secondary">{referral?.referringDentistPhone || '-'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Clinical Information */}
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                <Icon name="FileText" size={20} className="text-accent" />
                Informations cliniques
              </h3>
              <div className="bg-card-elevated border border-white/8 p-4 rounded-xl space-y-4">
                <div>
                  <p className="text-xs text-muted mb-2">Numéros de dents</p>
                  <div className="flex flex-wrap gap-2">
                    {referral?.toothNumbers?.map((tooth, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 rounded-lg bg-accent/20 text-accent text-sm font-medium"
                      >
                        {tooth}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted mb-2">Raisons de l'adressage</p>
                  <div className="flex flex-wrap gap-2">
                    {referral?.referralReasons?.map((reason, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 rounded-lg bg-primary/20 text-primary text-sm"
                      >
                        {reason}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted mb-2">Notes cliniques</p>
                  <p className="text-sm text-secondary whitespace-pre-wrap">
                    {referral?.clinicalNotes || 'Aucune note'}
                  </p>
                </div>

                {/* AI Analysis Results - REMOVED: OpenAI columns no longer exist */}
                {/* System now uses ONLY OpenAI for urgency classification via Edge Function */}
                {/* The urgency field (routine/urgent/emergency) is displayed in the status badge above */}
              </div>
            </div>

            {/* Imaging Information */}
            {(referral?.imagingDownloadLink || referral?.imagingCenter) && (
              <div>
                <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                  <Icon name="Image" size={20} className="text-accent" />
                  Imagerie
                </h3>
                <div className="bg-card-elevated border border-white/8 p-4 rounded-xl space-y-3">
                  {referral?.imagingDownloadLink && (
                    <div>
                      <p className="text-xs text-muted mb-1">Lien de téléchargement</p>
                      <a
                        href={referral?.imagingDownloadLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        {referral?.imagingDownloadLink}
                      </a>
                    </div>
                  )}
                  {referral?.imagingPassword && (
                    <div>
                      <p className="text-xs text-muted mb-1">Mot de passe</p>
                      <p className="text-sm font-medium text-secondary">{referral?.imagingPassword}</p>
                    </div>
                  )}
                  {referral?.imagingCenter && (
                    <div>
                      <p className="text-xs text-muted mb-1">Centre d'imagerie</p>
                      <p className="text-sm font-medium text-secondary">{referral?.imagingCenter}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* PDF Document Section */}
            {referral?.pdfPath && !isPdfExpired() && (
              <div>
                <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                  <Icon name="FileText" size={20} className="text-accent" />
                  Document PDF
                </h3>
                <div className="bg-card-elevated border border-white/8 p-4 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-error/20 flex items-center justify-center">
                        <Icon name="FileText" size={24} className="text-error" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-secondary">
                          Adressage - {referral?.patientLastName} {referral?.patientFirstName}
                        </p>
                        <p className="text-xs text-muted mt-1">
                          Document contenant les informations du cas et photos du patient
                        </p>
                        <p className="text-xs text-muted mt-1 flex items-center gap-1">
                          <Icon name="Clock" size={12} className="text-muted" />
                          Stocké pour 90 jours • Expire le {formatDate(referral?.pdfExpiresAt)}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleDownloadPdf}
                      loading={downloadingPdf}
                      disabled={downloadingPdf}
                      iconName="Download"
                      variant="outline"
                    >
                      {downloadingPdf ? 'Chargement...' : 'Télécharger'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* PDF Expired Message */}
            {referral?.pdfPath && isPdfExpired() && (
              <div>
                <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                  <Icon name="FileText" size={20} className="text-accent" />
                  Document PDF
                </h3>
                <div className="bg-card-elevated border border-warning/20 p-4 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg bg-warning/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="AlertTriangle" size={24} className="text-warning" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-secondary mb-2">
                        PDF expiré et supprimé
                      </p>
                      <p className="text-xs text-muted leading-relaxed">
                        Le document PDF de ce cas a été automatiquement supprimé après la période de rétention de 90 jours (expiré le {formatDate(referral?.pdfExpiresAt)}). Les informations du cas restent disponibles ci-dessus.
                      </p>
                      <div className="mt-3 flex items-center gap-1.5 text-xs text-muted">
                        <Icon name="Info" size={12} className="text-muted" />
                        <span>Politique de conservation : Les PDFs sont stockés pendant 90 jours pour des raisons de conformité RGPD</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PDF Not Generated Message */}
            {!referral?.pdfPath && (
              <div>
                <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                  <Icon name="FileText" size={20} className="text-accent" />
                  Document PDF
                </h3>
                <div className="bg-card-elevated border border-muted/20 p-4 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg bg-muted/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="FileX" size={24} className="text-muted" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-secondary mb-2">
                        PDF non disponible
                      </p>
                      <p className="text-xs text-muted leading-relaxed">
                        Aucun document PDF n'a été généré pour ce cas. Le PDF est créé automatiquement lors de la finalisation de l'adressage par le dentiste.
                      </p>
                      <div className="mt-3 flex items-center gap-1.5 text-xs text-muted">
                        <Icon name="Info" size={12} className="text-muted" />
                        <span>Note : Les PDFs sont stockés pendant 90 jours après leur création</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Internal Notes */}
            <div>
              <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                <Icon name="StickyNote" size={20} className="text-accent" />
                Notes internes
              </h3>
              <div className="bg-card-elevated border border-white/8 p-4 rounded-xl">
                <textarea
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e?.target?.value)}
                  placeholder="Ajouter des notes internes..."
                  className="w-full h-32 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-secondary placeholder:text-muted focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 resize-none"
                />
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {lastSavedAt && (
                      <p className="text-xs text-muted">
                        Dernière sauvegarde : {formatTime(lastSavedAt)}
                      </p>
                    )}
                    {saveSuccess && (
                      <div className="flex items-center gap-1 text-xs text-green-500">
                        <Icon name="Check" size={14} />
                        <span>Enregistré</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={handleViewRevisionHistory}
                      iconName="History"
                      size="sm"
                    >
                      Historique
                    </Button>
                    <Button
                      onClick={handleSaveNotes}
                      loading={saving}
                      disabled={saving}
                      iconName="Save"
                    >
                      {saving ? 'Enregistrement…' : 'Enregistrer les notes'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-card/95 backdrop-blur-xl border-t border-white/8 p-6 flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Fermer
            </Button>
          </div>
        </div>
      </div>

      {/* Urgency Update Modal */}
      {showUrgencyModal && (
        <>
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]"
            onClick={() => setShowUrgencyModal(false)}
          />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div
              className="bg-card rounded-2xl max-w-md w-full shadow-2xl border border-white/8 p-6"
              onClick={(e) => e?.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
                  <Icon name="AlertCircle" size={20} className="text-purple-400" />
                  Modifier l'urgence
                </h3>
                <button
                  onClick={() => setShowUrgencyModal(false)}
                  className="w-8 h-8 rounded-lg bg-card-elevated hover:bg-card-hover flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={16} className="text-secondary" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Niveau d'urgence
                  </label>
                  <div className="space-y-2">
                    <button
                      onClick={() => setSelectedUrgency('routine')}
                      className={`w-full px-4 py-3 rounded-lg border transition-all flex items-center gap-3 ${
                        selectedUrgency === 'routine' ? 'bg-muted/20 border-muted/50' : 'bg-card-elevated border-white/8 hover:border-muted/30'
                      }`}
                    >
                      <Icon name="Circle" size={18} className="text-muted-foreground" />
                      <div className="text-left">
                        <div className="font-medium text-secondary">Routine</div>
                        <div className="text-xs text-muted">Planification normale (2-4 semaines)</div>
                      </div>
                    </button>
                    <button
                      onClick={() => setSelectedUrgency('urgent')}
                      className={`w-full px-4 py-3 rounded-lg border transition-all flex items-center gap-3 ${
                        selectedUrgency === 'urgent' ? 'bg-warning/20 border-warning/50' : 'bg-card-elevated border-white/8 hover:border-warning/30'
                      }`}
                    >
                      <Icon name="AlertCircle" size={18} className="text-warning" />
                      <div className="text-left">
                        <div className="font-medium text-secondary">Urgent</div>
                        <div className="text-xs text-muted">Prise en charge rapide (1-2 semaines)</div>
                      </div>
                    </button>
                    <button
                      onClick={() => setSelectedUrgency('emergency')}
                      className={`w-full px-4 py-3 rounded-lg border transition-all flex items-center gap-3 ${
                        selectedUrgency === 'emergency' ? 'bg-error/20 border-error/50' : 'bg-card-elevated border-white/8 hover:border-error/30'
                      }`}
                    >
                      <Icon name="AlertTriangle" size={18} className="text-error" />
                      <div className="text-left">
                        <div className="font-medium text-secondary">Urgence</div>
                        <div className="text-xs text-muted">Intervention immédiate (24-48h)</div>
                      </div>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Résumé de l'urgence (optionnel)
                  </label>
                  <input
                    type="text"
                    value={urgencySummary}
                    onChange={(e) => setUrgencySummary(e?.target?.value)}
                    placeholder="Ex: douleur intense, fracture, infection..."
                    className="w-full px-4 py-2 bg-card-elevated border border-white/8 rounded-lg text-secondary placeholder:text-muted focus:outline-none focus:border-purple-500/50 transition-colors"
                    maxLength={50}
                  />
                  <p className="text-xs text-muted mt-1">2-4 mots décrivant la raison</p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowUrgencyModal(false)}
                    className="flex-1 px-4 py-2 rounded-lg bg-card-elevated hover:bg-card-hover border border-white/8 text-secondary font-medium transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleUpdateUrgency}
                    disabled={saving}
                    className="flex-1 px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-600 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <>
                        <Icon name="Loader2" size={16} className="animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <Icon name="Check" size={16} />
                        Enregistrer
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Revision History Modal */}
      {showRevisionHistory && (
        <>
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60]"
            onClick={handleCloseRevisionHistory}
          />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div
              className="bg-card rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col"
              onClick={(e) => e?.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-card/95 backdrop-blur-xl border-b border-white/8 p-6 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-primary flex items-center gap-2">
                    <Icon name="History" size={24} className="text-accent" />
                    Historique des révisions
                  </h2>
                  <p className="text-sm text-muted mt-1">
                    {revisions?.length || 0} révision(s) enregistrée(s)
                  </p>
                </div>
                <button
                  onClick={handleCloseRevisionHistory}
                  className="w-10 h-10 rounded-xl bg-card-elevated hover:bg-card-hover flex items-center justify-center transition-colors border border-white/8"
                >
                  <Icon name="X" size={20} className="text-secondary" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {loadingRevisions ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : revisions?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="FileText" size={48} className="mx-auto text-muted mb-4" />
                    <p className="text-muted">Aucune révision disponible</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {revisions?.map((revision, index) => (
                      <div
                        key={revision?.id}
                        className={`bg-card-elevated border border-white/8 p-4 rounded-xl transition-all ${
                          selectedRevision?.id === revision?.id
                            ? 'ring-2 ring-primary/50 bg-primary/5' :'hover:bg-white/5'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                              <span className="text-sm font-semibold text-primary">
                                #{revision?.revisionNumber}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-secondary">
                                {revision?.changedByName}
                              </p>
                              <p className="text-xs text-muted">
                                {revision?.changedByEmail}
                              </p>
                              <p className="text-xs text-muted mt-1">
                                {formatDate(revision?.createdAt)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewRevision(revision)}
                              iconName={selectedRevision?.id === revision?.id ? "EyeOff" : "Eye"}
                            >
                              {selectedRevision?.id === revision?.id ? 'Masquer' : 'Voir'}
                            </Button>
                            {index !== 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRestoreRevision(revision)}
                                iconName="RotateCcw"
                              >
                                Restaurer
                              </Button>
                            )}
                          </div>
                        </div>
                        {selectedRevision?.id === revision?.id && (
                          <div className="mt-4 pt-4 border-t border-white/10">
                            <p className="text-xs text-muted mb-2">Contenu de la révision :</p>
                            <div className="bg-black/20 rounded-lg p-4 max-h-64 overflow-y-auto">
                              <p className="text-sm text-secondary whitespace-pre-wrap">
                                {revision?.content || '(Vide)'}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-card/95 backdrop-blur-xl border-t border-white/8 p-6 flex justify-end">
                <Button variant="outline" onClick={handleCloseRevisionHistory}>
                  Fermer
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ReferralDetailModal;